import lodash from 'lodash';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OUT_OF_STANDARD_CURRENCY } from '@cxc/core/enums';

import { API } from '@models/API';

import { LenderAPI } from '@services/API/trusts/lender';
import { CurrencyState } from '@services/state/currency-state/currency.state';
import { CutOffDateState } from '@services/state/cut-off-date-state/cut-off-date.state';
import { LenderState } from '@services/state/lender-state/lender.state';

/** The lender/cut-off/currency triple a resume payload belongs to. */
export interface OverviewSelection {
  lender: string;
  cutOff: string;
  currency: OUT_OF_STANDARD_CURRENCY;
}

/**
 * Owns the single `overview-resume` request of the General Overview page and
 * exposes each slice of the payload as a signal.
 *
 * This service is provided by `GeneralOverviewInsights`, not in the root
 * injector, on purpose: `CutOffDateState` and `CurrencyState` are shared with
 * the facility pages, so a root-provided instance would keep firing
 * `overview-resume` while the user is browsing a facility.
 *
 * Three operators carry the behaviour the page relies on:
 * - `distinctUntilChanged` drops the intermediate triples produced while the
 *   three source states settle during bootstrap, so only real selections hit
 *   the network.
 * - `switchMap` cancels the in-flight request when the selection changes, so a
 *   slow response can no longer land on top of a newer one and leave the page
 *   showing another lender's data.
 * - `catchError` *inside* the inner observable keeps a failed request from
 *   terminating the outer subscription. Without it a single 500 would stop the
 *   page requesting anything for the rest of its life (LEN-2078): the error
 *   would propagate out of `switchMap` and complete the subscriber.
 */
@Injectable()
export class OverviewResumeState {
  private lenderAPIOverview = inject(LenderAPI.Overview);
  private cutOffDateState = inject(CutOffDateState);
  private currencyState = inject(CurrencyState);
  private lenderState = inject(LenderState);

  private _resume = signal<
    API.Trusts.Lender.Overview.Resume.Response | undefined
  >(undefined);
  private _loadedSelection = signal<OverviewSelection | undefined>(undefined);
  private _pendingSelection = signal<OverviewSelection | undefined>(undefined);
  private _failed = signal(false);

  /** The whole payload. Undefined until the first response lands. */
  public resume = this._resume.asReadonly();

  /** The selection the current `resume` belongs to — never ahead of it. */
  public selection = this._loadedSelection.asReadonly();

  /**
   * The selection the user is on *now*, set before the request goes out.
   * Labels and export filenames must read this one: `selection` only catches
   * up when the payload lands, so using it for UI text pairs the previous
   * lender's name with the new cut-off date while the request is in flight.
   */
  public pendingSelection = this._pendingSelection.asReadonly();

  /**
   * True when the request for `pendingSelection` failed. The page renders an
   * error in place of the data, rather than leaving the previous lender's
   * figures under the new lender's header — the integrity bug in LEN-2078.
   */
  public failed = this._failed.asReadonly();

  // Slices. Every one stays undefined until a response lands, so consumers can
  // tell "not loaded yet" apart from "loaded and empty".
  public coreMetrics = computed(() => this._resume()?.core_metrics);
  public operations = computed(() => this._resume()?.operations);
  public carrouselCharts = computed(() => this._resume()?.carrousel_charts);

  constructor() {
    combineLatest([
      this.cutOffDateState.currentPeriodISO$,
      this.currencyState.currencySelected$,
      this.lenderState.selected$,
    ])
      .pipe(
        filter(val => !!val[0] && !!val[1] && !!val[2]),
        map(
          (val): OverviewSelection => ({
            lender: val[2]!,
            cutOff: val[0]!,
            currency: val[1]!.currency,
          })
        ),
        distinctUntilChanged<OverviewSelection>(lodash.isEqual),
        // Drop the previous lender's data as soon as the selection changes, so
        // the page cannot show stale figures under a new header, and publish
        // the new selection immediately for the UI labels.
        tap(selection => {
          this._pendingSelection.set(selection);
          this._resume.set(undefined);
          this._failed.set(false);
        }),
        switchMap(selection =>
          this.lenderAPIOverview
            .getResume(selection.lender, selection.cutOff, selection.currency)
            .pipe(
              map(resume => ({ selection, resume })),
              // Scoped to the inner observable so the outer subscription
              // survives. `switchMap` still cancels this on a newer selection,
              // so a late failure cannot clear a newer payload.
              catchError(() => of({ selection, resume: undefined }))
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ selection, resume }) => {
        if (!resume) {
          this._failed.set(true);
          return;
        }
        // Set together so `selection` and `resume` are never out of step.
        this._loadedSelection.set(selection);
        this._resume.set(resume);
      });
  }
}
